import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select, SelectItem, Stack, Tile } from "@carbon/react";
import type { ImportResolveType, UnresolvedValue } from "../../types";

type Option = { id: string; label: string };

type Props = {
  values: UnresolvedValue[];
  options: Record<ImportResolveType | "NONE", Option[]>;
  isResolving: boolean;
  onResolve: (resolutions: Record<string, Record<string, string>>) => void;
};

/**
 * The values we could not match, asked once per distinct value rather than once per row.
 *
 * A file of 693 lines carrying three buyer surnames is three questions. Answering one moves every
 * row that carried it.
 */
const UnresolvedValuesPanel = ({
  values,
  options,
  isResolving,
  onResolve,
}: Props) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<
    Record<string, Record<string, string>>
  >({});

  const byColumn = useMemo(() => {
    const grouped = new Map<string, UnresolvedValue[]>();
    values.forEach((value) => {
      const list = grouped.get(value.column) ?? [];
      list.push(value);
      grouped.set(value.column, list);
    });
    return Array.from(grouped.entries());
  }, [values]);

  /**
   * Records a choice, and treats going back to "- choose -" as taking it back.
   *
   * Without that, re-selecting the placeholder would send an empty id to the server as though it
   * were a decision.
   */
  const choose = (column: string, value: string, id: string) =>
    setPending((current) => {
      const forColumn = { ...(current[column] ?? {}) };
      if (id) {
        forColumn[value] = id;
      } else {
        delete forColumn[value];
      }
      const next = { ...current, [column]: forColumn };
      if (Object.keys(forColumn).length === 0) {
        delete next[column];
      }
      return next;
    });

  const pendingCount = Object.values(pending).reduce(
    (total, chosen) => total + Object.keys(chosen).length,
    0
  );

  const apply = () => {
    onResolve(pending);
    // Cleared so a second Apply does not resend decisions the server already has, and so the
    // button goes quiet until there is something new to say.
    setPending({});
  };

  if (byColumn.length === 0) return null;

  return (
    <Tile className="pr-import__panel">
      <div className="pr-import__panel-head">
        <h3 className="pr-import__title">
          {t("purchaseRequests.import.unresolved")}
        </h3>
        <p className="pr-import__hint">
          {t("purchaseRequests.import.unresolvedHint")}
        </p>
      </div>

      <Stack gap={6}>
        {byColumn.map(([column, columnValues]) => (
          <div key={column} className="pr-import__unresolved-column">
            <p className="pr-import__subtitle">
              {column}{" "}
              <span className="pr-import__muted">
                {t("purchaseRequests.import.valuesAndRows", {
                  values: columnValues.length,
                  rows: columnValues.reduce(
                    (total, v) => total + v.rowCount,
                    0
                  ),
                })}
              </span>
            </p>
            {columnValues.map((value) => (
              <div
                key={`${column}-${value.value}`}
                className="pr-import__unresolved-row"
              >
                <code className="pr-import__code">{value.value}</code>
                <span className="pr-import__muted">
                  {t("purchaseRequests.import.valueTo")}
                </span>
                <Select
                  id={`pr-import-resolve-${column}-${value.value}`}
                  labelText={t("purchaseRequests.import.resolveLabel", {
                    value: value.value,
                  })}
                  hideLabel
                  size="sm"
                  className="pr-import__target-select"
                  value={pending[column]?.[value.value] ?? ""}
                  onChange={(event) =>
                    choose(column, value.value, event.target.value)
                  }
                >
                  <SelectItem
                    value=""
                    text={t("purchaseRequests.import.chooseOne")}
                  />
                  {(options[value.resolveType ?? "NONE"] ?? []).map(
                    (option) => (
                      <SelectItem
                        key={option.id}
                        value={option.id}
                        text={option.label}
                      />
                    )
                  )}
                </Select>
                <span className="pr-import__muted">
                  {t("purchaseRequests.import.rowCount", {
                    count: value.rowCount,
                  })}
                </span>
              </div>
            ))}
          </div>
        ))}
      </Stack>

      <div className="pr-import__actions">
        <Button
          size="sm"
          onClick={apply}
          disabled={pendingCount === 0 || isResolving}
        >
          {t("purchaseRequests.import.applyDecisions")}
        </Button>
      </div>
    </Tile>
  );
};

export default UnresolvedValuesPanel;
